// ─────────────────────────────────────────────
//  TermPicker.jsx
//  Selector de plazo: días 1–29 → meses 1–120
//
//  Slider positions:
//   1–29  → días   (1 a 29 días)
//  30–149 → meses  (1 a 120 meses / 10 años)
// ─────────────────────────────────────────────

import React from 'react';
import styles from './TermPicker.module.css';

const TERM_MIN = 1;
const TERM_MAX = 149; // 29 días + 120 meses

// pos ≤ 29 → días; pos > 29 → meses (pos - 29)
export function termPosToYears(pos) {
	if (pos <= 29) return pos / 365;
	return (pos - 29) / 12;
}

export function formatTerm(pos) {
	if (pos <= 29) {
		return `${pos} ${pos === 1 ? 'día' : 'días'}`;
	}
	const months = pos - 29;
	if (months < 12) return `${months} ${months === 1 ? 'mes' : 'meses'}`;
	const years = Math.floor(months / 12);
	const rem = months % 12;
	if (rem === 0) return `${years} ${years === 1 ? 'año' : 'años'}`;
	return `${years}a ${rem}m`;
}

// Quick-access terms — pos = days or 29 + months
const QUICK_TERMS = [
	{ label: '1m',  pos: 30  },   // 29 + 1
	{ label: '6m',  pos: 35  },   // 29 + 6
	{ label: '1a',  pos: 41  },   // 29 + 12
	{ label: '2a',  pos: 53  },   // 29 + 24
	{ label: '5a',  pos: 89  },   // 29 + 60
	{ label: '10a', pos: 149 },   // 29 + 120
];

export default function TermPicker({ termPos, onChange }) {
	const fillPct = ((termPos - TERM_MIN) / (TERM_MAX - TERM_MIN)) * 100;
	const isDays  = termPos <= 29;

	return (
		<section className={styles.card} aria-label="Plazo de inversión">

			<div className={styles.header}>
				<div>
					<p className={styles.label}>
						Plazo{isDays && <span className={styles.daysBadge}>días</span>}
					</p>
					<p className={styles.termDisplay} aria-live="polite">
						{formatTerm(termPos)}
					</p>
				</div>
			</div>

			<div className={styles.trackWrap}>
				<div className={styles.trackBg}>
					<div className={styles.trackFill} style={{ width: `${fillPct}%` }} />
				</div>
				<input
					type="range"
					className={styles.slider}
					min={TERM_MIN}
					max={TERM_MAX}
					step={1}
					value={termPos}
					aria-label="Plazo de inversión"
					aria-valuemin={TERM_MIN}
					aria-valuemax={TERM_MAX}
					aria-valuenow={termPos}
					aria-valuetext={formatTerm(termPos)}
					onChange={(e) => onChange(Number(e.target.value))}
				/>
			</div>

			<div
				className={styles.quickBtns}
				role="group"
				aria-label="Plazos de acceso rápido"
			>
				{QUICK_TERMS.map((t) => (
					<button
						key={t.pos}
						className={`${styles.quickBtn} ${termPos === t.pos ? styles.quickBtnActive : ''}`}
						onClick={() => onChange(t.pos)}
						type="button"
						aria-pressed={termPos === t.pos}
						aria-label={formatTerm(t.pos)}
					>
						{t.label}
					</button>
				))}
			</div>

		</section>
	);
}
